import { coerceText } from '../../utils/coerce-text';

export type SubmitFieldMapping = { apiKey: string; formKey: string };

/** Normalize workflow `payloadMapping` (object or array form) into flat pairs. */
export function parseSubmitPayloadMapping(raw: unknown): SubmitFieldMapping[] {
  if (Array.isArray(raw)) {
    return raw
      .filter((row) => row && typeof row === 'object' && 'key' in row && 'value' in row)
      .map((row) => ({
        apiKey: coerceText((row as { key: unknown }).key),
        formKey: coerceText((row as { value: unknown }).value),
      }))
      .filter((row) => row.apiKey && row.formKey);
  }

  if (raw && typeof raw === 'object') {
    return Object.entries(raw as Record<string, unknown>)
      .map(([apiKey, formKey]) => ({
        apiKey,
        formKey: coerceText(formKey),
      }))
      .filter((row) => row.apiKey && row.formKey);
  }

  return [];
}

/** Map form/modal values to API payload using workflow submit-node field mapping. */
export function buildSubmitPayload(
  mappings: SubmitFieldMapping[],
  formValues: Record<string, unknown>,
  extras: Record<string, unknown> = {},
): Record<string, unknown> {
  const payload: Record<string, unknown> = { ...extras };

  for (const { apiKey, formKey } of mappings) {
    if (!(formKey in formValues)) continue;
    const value = formValues[formKey];
    if (value === undefined) continue;
    setNestedValue(payload, apiKey, value);
  }

  return payload;
}

function isNumericPathSegment(segment: string): boolean {
  return /^\d+$/.test(segment);
}

function createNestedObject(): Record<string, unknown> {
  return {};
}

function createNestedArray(): unknown[] {
  return [];
}

function isNestedContainer(value: unknown): value is Record<string, unknown> | unknown[] {
  return typeof value === 'object' && value !== null;
}

function ensureNestedSlot(
  container: Record<string, unknown> | unknown[],
  key: string,
  nextIsIndex: boolean,
): Record<string, unknown> | unknown[] {
  if (Array.isArray(container)) {
    const idx = Number(key);
    if (!isNestedContainer(container[idx])) {
      container[idx] = nextIsIndex ? createNestedArray() : createNestedObject();
    }
    return container[idx] as Record<string, unknown> | unknown[];
  }

  if (!isNestedContainer(container[key])) {
    container[key] = nextIsIndex ? createNestedArray() : createNestedObject();
  }
  return container[key] as Record<string, unknown> | unknown[];
}

function assignNestedValue(
  container: Record<string, unknown> | unknown[],
  key: string,
  value: unknown,
): void {
  if (Array.isArray(container)) {
    container[Number(key)] = value;
    return;
  }
  container[key] = value;
}

function setNestedValue(
  target: Record<string, unknown>,
  path: string,
  value: unknown,
): void {
  const parts = path.split('.');
  let current: Record<string, unknown> | unknown[] = target;

  for (let i = 0; i < parts.length - 1; i++) {
    const nextIsIndex = isNumericPathSegment(parts[i + 1]);
    current = ensureNestedSlot(current, parts[i], nextIsIndex);
  }

  assignNestedValue(current, parts.at(-1) ?? '', value);
}
